import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

interface ThemeConfig {
  primary_color: string;
  font_family: string;
  logo_url: string | null;
}

interface TenantConfig {
  id: string;
  name: string;
  custom_domain: string | null;
  logo_url: string | null;
  theme_config: ThemeConfig;
  white_label: boolean;
  favicon_url: string | null;
  login_background_url: string | null;
  tagline: string | null;
}

interface FeatureFlags {
  [key: string]: boolean;
}

interface TenantContextType {
  tenant: TenantConfig | null;
  themeConfig: ThemeConfig;
  communityName: string | null;
  isLoading: boolean;
  isWhiteLabel: boolean;
  featureFlags: FeatureFlags;
  hasFeature: (flagName: string) => boolean;
  refetchTenant: () => Promise<void>;
}

const defaultTheme: ThemeConfig = {
  primary_color: '#D4AF37',
  font_family: 'Playfair Display',
  logo_url: null,
};

const TenantContext = createContext<TenantContextType>({
  tenant: null,
  themeConfig: defaultTheme,
  communityName: null,
  isLoading: true,
  isWhiteLabel: false,
  featureFlags: {},
  hasFeature: () => true, // Default to true to prevent accidental lockouts
  refetchTenant: async () => {},
});

export const useTenant = () => useContext(TenantContext);

// Also export useTheme as alias for backward compatibility
export const useTheme = () => {
  const ctx = useContext(TenantContext);
  return {
    themeConfig: ctx.themeConfig,
    communityName: ctx.communityName,
    isLoading: ctx.isLoading,
  };
};

function hexToHSL(hex: string): string {
  hex = hex.replace(/^#/, '');
  
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const applyTheme = (config: ThemeConfig) => {
  const root = document.documentElement;
  
  if (config.primary_color) {
    const hsl = hexToHSL(config.primary_color);
    root.style.setProperty('--primary', hsl);
    root.style.setProperty('--ring', hsl);
  }

  if (config.font_family) {
    root.style.setProperty('--font-display', `"${config.font_family}", serif`);
  }
};

const applyFavicon = (faviconUrl: string | null) => {
  if (!faviconUrl) return;
  
  let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
};

// Get tenant from current domain
const getCurrentDomain = (): string => {
  const hostname = window.location.hostname;
  // Skip localhost and lovable domains
  if (hostname === 'localhost' || hostname.includes('lovable.app')) {
    return '';
  }
  return hostname;
};

// Default feature flags configuration
const DEFAULT_FEATURE_FLAGS = [
  { name: 'rentals', enabled: true },
  { name: 'long_term_leasing', enabled: true },
  { name: 'sales', enabled: true },
  { name: 'concierge', enabled: true },
  { name: 'smart_home', enabled: false },
  { name: 'amenity_booking', enabled: true },
  { name: 'hoa_payments', enabled: true },
  { name: 'guest_passes', enabled: true },
  { name: 'announcements', enabled: true },
  { name: 'messaging', enabled: true },
  { name: 'vendors', enabled: true },
  { name: 'ai_assistant', enabled: false },
];

export const TenantProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [themeConfig, setThemeConfig] = useState<ThemeConfig>(defaultTheme);
  const [communityName, setCommunityName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({});

  // Auto-initialize feature flags for a community
  const autoInitializeFeatureFlags = async (communityId: string) => {
    try {
      const flagsToInsert = DEFAULT_FEATURE_FLAGS.map(flag => ({
        community_id: communityId,
        flag_name: flag.name,
        is_enabled: flag.enabled,
      }));

      const { error } = await supabase
        .from('feature_flags')
        .upsert(flagsToInsert, { onConflict: 'community_id,flag_name' });

      if (error) {
        console.error('Error auto-initializing feature flags:', error);
        return;
      }

      // Set the flags in state
      const flagMap: FeatureFlags = {};
      DEFAULT_FEATURE_FLAGS.forEach(f => { flagMap[f.name] = f.enabled; });
      setFeatureFlags(flagMap);

      console.log('Feature flags auto-initialized for community:', communityId);
    } catch (err) {
      console.error('Failed to auto-initialize feature flags:', err);
    }
  };

  // Subscribe to auth state independently
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch tenant by domain (runs before auth)
  useEffect(() => {
    const fetchTenantByDomain = async () => {
      const domain = getCurrentDomain();
      if (!domain) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .rpc('get_community_by_domain', { _domain: domain });

        if (error || !data || data.length === 0) {
          console.log('No tenant found for domain:', domain);
          setIsLoading(false);
          return;
        }

        const tenantData = data[0];
        const config: TenantConfig = {
          id: tenantData.id,
          name: tenantData.name,
          custom_domain: tenantData.custom_domain,
          logo_url: tenantData.logo_url,
          theme_config: (tenantData.theme_config as unknown as ThemeConfig) || defaultTheme,
          white_label: tenantData.white_label,
          favicon_url: tenantData.favicon_url,
          login_background_url: tenantData.login_background_url,
          tagline: tenantData.tagline,
        };

        setTenant(config);
        setCommunityName(config.name);
        setThemeConfig(config.theme_config);
        applyTheme(config.theme_config);
        applyFavicon(config.favicon_url);

        // Update document title for white-label
        if (config.white_label && config.name) {
          document.title = config.name;
        }
      } catch (err) {
        console.error('Error fetching tenant by domain:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTenantByDomain();
  }, []);

  // Fetch tenant from user membership (fallback when no domain match)
  useEffect(() => {
    const fetchTenantFromUser = async () => {
      if (!user || tenant) return; // Skip if already have tenant from domain

      try {
        setIsLoading(true);
        
        const { data: membership, error: memError } = await supabase
          .from('community_memberships')
          .select('community_id')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .limit(1)
          .single();

        if (memError || !membership) {
          applyTheme(defaultTheme);
          setIsLoading(false);
          return;
        }

        const { data: community, error: comError } = await supabase
          .from('communities')
          .select('id, name, theme_config, logo_url, custom_domain, white_label, favicon_url, tagline')
          .eq('id', membership.community_id)
          .single();

        if (comError || !community) {
          applyTheme(defaultTheme);
          setIsLoading(false);
          return;
        }

        const config: TenantConfig = {
          id: community.id,
          name: community.name,
          custom_domain: community.custom_domain,
          logo_url: community.logo_url,
          theme_config: (community.theme_config as unknown as ThemeConfig) || defaultTheme,
          white_label: community.white_label,
          favicon_url: community.favicon_url,
          login_background_url: null,
          tagline: community.tagline,
        };

        setTenant(config);
        setCommunityName(community.name);
        setThemeConfig(config.theme_config);
        applyTheme(config.theme_config);
        applyFavicon(config.favicon_url);

        // Fetch feature flags
        const { data: flags } = await supabase
          .from('feature_flags')
          .select('flag_name, is_enabled')
          .eq('community_id', community.id);

        if (flags && flags.length > 0) {
          const flagMap: FeatureFlags = {};
          flags.forEach(f => { flagMap[f.flag_name] = f.is_enabled; });
          setFeatureFlags(flagMap);
        } else {
          // No flags exist - auto-initialize them for this community
          console.log('No feature flags found, auto-initializing for community:', community.id);
          await autoInitializeFeatureFlags(community.id);
        }
      } catch (err) {
        console.error('Error fetching tenant:', err);
        applyTheme(defaultTheme);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTenantFromUser();
  }, [user, tenant]);

  const hasFeature = (flagName: string): boolean => {
    // Default to true if flag doesn't exist - prevents accidental lockouts
    // for communities without initialized flags
    return featureFlags[flagName] ?? true;
  };

  const refetchTenant = async () => {
    if (!tenant?.id) return;
    
    const { data: flags } = await supabase
      .from('feature_flags')
      .select('flag_name, is_enabled')
      .eq('community_id', tenant.id);

    if (flags) {
      const flagMap: FeatureFlags = {};
      flags.forEach(f => { flagMap[f.flag_name] = f.is_enabled; });
      setFeatureFlags(flagMap);
    }
  };

  return (
    <TenantContext.Provider value={{
      tenant,
      themeConfig,
      communityName,
      isLoading,
      isWhiteLabel: tenant?.white_label ?? false,
      featureFlags,
      hasFeature,
      refetchTenant,
    }}>
      {children}
    </TenantContext.Provider>
  );
};
